import type { StringKey } from '@ipfabric/utils';
import classNames from 'classnames';
import { FormField } from 'components/FormField/FormField';
import {
  type GetSelectOptionValueFunction,
  Select,
  type SelectProps,
  getSelectOptionValueDefault,
} from 'components/Select/Select';
import type { FormikProps } from 'formik';
import type { JSX, ReactNode } from 'react';
import { fieldHasError } from 'utils/formik/fieldHasError';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

type Props<TFormikValues, TValue> = Omit<
  SelectProps<TValue>,
  'value' | 'onChange'
> & {
  label?: ReactNode;
  formik: FormikProps<TFormikValues>;
  name: StringKey<TFormikValues> | NestedFormikName;
  dataTestId?: string;
  onChange?: (value: TValue | null) => void;
};

export const FormikSelectField = <TFormikValues, TValue>({
  label,
  formik,
  name,
  onChange,
  // Need to assign default comparable option value getter since TS is not able
  // to decide if it can be optional from generic TValue for some reason
  getOptionValue = getSelectOptionValueDefault as GetSelectOptionValueFunction<TValue | null>,
  ...props
}: Props<TFormikValues, TValue>): JSX.Element => {
  const field = formik.getFieldProps<TValue | null>(name);
  const { error } = formik.getFieldMeta(name as string);
  const hasError = fieldHasError<TFormikValues>(formik, name);

  const handleChange = (value: TValue | null) => {
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });
    onChange?.(value);
    formik.validateField(field.name);
  };

  const handleBlur = () => {
    field.onBlur({
      target: {
        name: field.name,
      },
    });
  };

  return (
    <FormField
      className={classNames(hasError && 'formik-error-scroll-anchor')}
      label={label}
      error={hasError ? error : undefined}
    >
      <Select
        {...props}
        hasError={hasError}
        onChange={handleChange}
        onBlur={handleBlur}
        value={field.value}
        getOptionValue={getOptionValue}
      />
    </FormField>
  );
};
